import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reduxForm, Field, Fields, FieldArray, SubmissionError } from 'redux-form/immutable';
import { Radio, RadioGroup, FormControlLabel, Tooltip } from '@material-ui/core/';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormLabel from '@material-ui/core/FormLabel';
import moment, { MomentSetObject } from 'moment';
import { zonedTimeToUtc } from 'date-fns-tz';
import { InjectedFormProps } from 'redux-form/lib/reduxForm';
import { Map as ImmutableMap, List } from 'immutable';
import { Alert } from '@mui/material';

import { RenderHtml } from 'components/RenderHtml';
import PhoneNumber from 'retired/PhoneNumberField';
import FormTextFieldWithCheckbox from 'retired/FormTextFieldWithCheckbox';
import FormDoubleTextFieldWithCheckbox from 'retired/FormDoubleTextFieldWithCheckbox';
import FormDateWithStartAndEndTime from 'retired/FormDateWithStartAndEndTime';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import FormTextField from 'retired/FormTextField';
import FormRoles from 'retired/FormRoles';
import FormPartnerSelector from 'retired/FormPartnerSelector';
import FormLocationField from 'retired/FormLocationField';
import FormCheckbox from 'retired/FormCheckbox';
import FormPublishingStatus from 'retired/FormPublishingStatus';
import FormValueSelector from 'retired/FormValueSelector';
import FormPictureSelector from 'retired/FormPictureSelector';
import FormFormsSelector from 'retired/FormFormsSelector';
import FormEditor from 'retired/FormEditor';
import FormRadioButton from 'retired/FormRadioButton';
import { red } from 'utils/color-styles';
import { fieldArrayHasErrors } from 'utils/reduxForm';
import { PureRender } from 'utils/component-helpers';
import addressToLocation from 'utils/addressToLocation';
import FormLocationSelector from 'retired/FormLocationSelector';
import { getTimeZoneFromGoogle } from 'utils/getTimeZoneFromGoogle';
import { scrollToError } from 'utils/scroll.helper';
import { useQueryParams } from 'utils/useQueryParams';
import {
  SelectCommunities,
  SelectOrganizingCommunity,
  useCommunitiesList,
} from 'containers/DeedFormPage/CommunitySelect';
import { Features } from 'utils/features';
import FormSelectedAndOptedInCompanies from 'retired/FormSelectedAndOptedInCompanies';
import type { State } from 'reducers';
import {
  selectAccessibleUserLocations,
  selectOrganization,
  selectUser,
} from 'entities/user/selectors';
import { selectCampaigns } from 'entities/campaign/selectors';
import { selectCategories, selectERGs, selectPillars, selectSDGs } from 'entities/cause/selectors';
import { selectForms } from 'entities/form/selectors';
import { selectPictures } from 'entities/picture/selectors';
import { selectPartners } from 'entities/partners/selectors';
import { useDeedFormQueryParams } from 'containers/DeedFormPage/queryParams';
import { type AutoStep, AutoStepper } from 'retired/Stepper/AutoStepper';
import { OrganizationType } from 'generated/graphql';
import { NonprofitEntityType } from 'entities/organization/model';

import { type FormData, formSyncErrors, pickFormFields, valueSelector } from '../formUtils';
import * as Actions from '../actions';
import { FORM_IDENTIFIER } from '../constants';
import { selectPartnersLoading, selectUploadedFile } from '../selectors';
import { DisableWaitlistCheckbox } from '../DisableWaitlistCheckbox';
import { RegistrationDeadline } from '../RegistrationDeadline';
import { shareTooltip } from '../copy';
import {
  isAddress,
  isEmail,
  isNumber,
  isPhone,
  isURL,
  maxLength5,
  maxLength5000,
  notEmpty,
  required,
  requiredRadioButton,
} from '../validators';
import { ProvisionalNonprofitDataBanner } from '../ProvisionalNonprofitDataBanner';
import { EventDurationText } from '../EventDurationText';
import LongEventWarningModal from '../LongEventWarningModal';

export type DeedVolunteerFormProps = InjectedFormProps<FormData>;

const formValuesSelector = pickFormFields([
  'attendeeLimit',
  'backgroundCheckLink',
  'categories',
  'companyOrPublic',
  'sharedWithCompanies',
  'description',
  'donationLink',
  'donationText',
  'duties',
  'endingAt',
  'featured',
  'id',
  'location',
  'mainPicture',
  'name',
  'organization',
  'partner',
  'partnerEmail',
  'partnerName',
  'roles',
  'pictures',
  'public',
  'startingAt',
  'virtual',
]);

const DeedVolunteerForm = ({
  dirty,
  handleSubmit,
  change,
}: DeedVolunteerFormProps): JSX.Element | null => {
  const { deedId, deedType, deed, past } = useDeedFormQueryParams();
  const dispatch = useDispatch();
  const { communities } = useCommunitiesList();
  const [timeZone, setTimeZone] = useState<string | undefined>(undefined);
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
  const [pendingSubmission, setPendingSubmission] = useState<any>(null);
  const partnersLoading = useSelector(selectPartnersLoading);
  const queryParams = useQueryParams();
  const duplicate = queryParams.get('duplicate');

  const user = useSelector(selectUser);
  const campaigns = useSelector(selectCampaigns);
  const pillars = useSelector(selectPillars);
  const ERGs = useSelector(selectERGs);
  const categories = useSelector((state: State) => selectCategories(state, true));
  const SDGs = useSelector(selectSDGs);
  const forms = useSelector(selectForms);
  const pictures = useSelector(selectPictures);
  const uploadedFile = useSelector(selectUploadedFile);
  const organization = useSelector(selectOrganization);
  const partners = useSelector(selectPartners).filter((_, key) => {
    // Temporary hotfix per SC-77895
    if (key !== '62ab3acaa0084555b4e95d23') {
      return true;
    }

    return ![
      NonprofitEntityType.governmentEntity,
      NonprofitEntityType.memberAssociatedEntity,
    ].includes(organization?.nonprofitEntityType);
  });
  const locations = useSelector(selectAccessibleUserLocations);

  const formValues = useSelector(formValuesSelector);

  const status = useSelector((state: State) => valueSelector(state, 'status')) || 'draft';
  const errors = useSelector(formSyncErrors);
  const isCompany = organization?.isCompany();
  const invalid = Boolean(
    (ImmutableMap.isMap(errors) ? errors.size !== 0 : Object.keys(errors).length) ||
      fieldArrayHasErrors(errors.roles) ||
      fieldArrayHasErrors(errors.formQuestions)
  );

  const deedApprovalRequired =
    partners?.get(formValues?.partner)?.deedApprovalRequired &&
    user?.organization?.type === OrganizationType.Nonprofit;

  const isCommunityFeatureEnabled = user?.hasFeature(Features.COMMUNITIES);

  const handleLocationSelect = (address) =>
    addressToLocation(address).subscribe(({ location }) => {
      getTimeZoneFromGoogle(location).subscribe((result) => {
        if (!result) {
          throw Error('Error getting timezone data');
        }
        setTimeZone(result.timeZoneId);
      });
    });

  useEffect(() => {
    if (deed?.timeZone) {
      setTimeZone(deed.timeZone);
    }
  }, [deed]);

  const nonprofitForms =
    isCompany && deed?.forms?.filter((form) => form.organization === deed.organization?.id);

  const isExternal = formValues.id?.startsWith('ext-') || !!deed?.externalId;
  const isGoodera = deed?.provider === 'Goodera';
  const enableValidation = status !== 'draft';

  const ensureCompanyOnlyDeeds = organization?.hasSetting('ensureCompanyOnlyDeeds');

  useEffect(() => {
    if (ensureCompanyOnlyDeeds && (formValues.id === 'new' || formValues.id === 'past')) {
      change('requireAuthentication', true);
      change('public', false);
    }
  }, [ensureCompanyOnlyDeeds]);

  useEffect(() => {
    // @NOTE-RT: When an NPO organizer with a corporate partner removes that partner, make them reselect `formValues.companyOrPublic`
    if (!isCompany && !formValues.partner && formValues.companyOrPublic === 'company') {
      change('companyOrPublic', null);
    }
  }, [formValues.companyOrPublic, isCompany, formValues.partner]);

  useEffect(() => {
    // @NOTE-RT: When we have roles, just auto-compute the `attendeeLimit`
    if (formValues.roles?.size > 0) {
      change(
        'attendeeLimit',
        formValues.roles.reduce(
          (total, role) => total + (role?.requiredAmount ? Number(role?.requiredAmount) : 0),
          0
        )
      );
    }
  }, [formValues.roles]);

  const [eventDuration, setEventDuration] = useState<number | null>(0);

  useEffect(() => {
    // @NOTE-RT: When we have roles, we need to adjust their `startingAt` and `endingAt` dates in line with the event form. This will go away once we introduce multi-day events, but for the moment we just show time fields that are contextualized by the overall `Event#startingAt` and `Event#endingAt`
    if (formValues.roles?.size > 0) {
      const eventStartingAt = formValues?.startingAt && moment(formValues.startingAt);
      const eventEndingAt = formValues?.endingAt && moment(formValues.endingAt);

      // @NOTE-DP: just a safeguard. We should never end up in this situation, the date picker shouldn't allow it.
      if (eventStartingAt?.isAfter(eventEndingAt)) {
        return;
      }

      formValues.roles.forEach((role, i) => {
        const roleStartingAt = role?.startingAt && moment(role?.startingAt);
        const roleEndingAt = role?.endingAt && moment(role?.endingAt);
        const roleStartingAtIsBetweenEventStartingAtAndEndingAt =
          (eventStartingAt &&
            eventEndingAt &&
            roleStartingAt &&
            roleStartingAt.isBetween(eventStartingAt, eventEndingAt, undefined, '[]')) ||
          false;
        const roleEndingAtIsBetweenEventStartingAtAndEndingAt =
          (eventStartingAt &&
            roleEndingAt &&
            roleEndingAt &&
            roleEndingAt.isBetween(eventStartingAt, eventEndingAt, undefined, '[]')) ||
          false;

        if (
          eventStartingAt &&
          eventEndingAt &&
          roleStartingAt &&
          !roleStartingAtIsBetweenEventStartingAtAndEndingAt
        ) {
          const timeComponent: MomentSetObject = {};
          timeComponent.hour = roleStartingAt.hour();
          timeComponent.minutes = roleStartingAt.minutes();
          const shiftedStartingAt = eventEndingAt.clone().set(timeComponent);
          change(
            `roles.${i}.startingAt`,
            shiftedStartingAt.isBetween(eventStartingAt, eventEndingAt, undefined, '[]')
              ? shiftedStartingAt
              : eventStartingAt
          );
        }

        if (
          eventStartingAt &&
          eventEndingAt &&
          roleEndingAt &&
          !roleEndingAtIsBetweenEventStartingAtAndEndingAt
        ) {
          const timeComponent: MomentSetObject = {};
          timeComponent.hour = roleEndingAt.hour();
          timeComponent.minutes = roleEndingAt.minutes();
          const shiftedEndingAt = eventEndingAt.clone().set(timeComponent);
          change(
            `roles.${i}.endingAt`,
            shiftedEndingAt.isBetween(eventStartingAt, eventEndingAt, undefined, '[]')
              ? shiftedEndingAt
              : eventEndingAt
          );
        }
      });
    }
    setEventDuration(moment(formValues.endingAt).diff(moment(formValues.startingAt), 'hours'));
  }, [formValues.startingAt, formValues.endingAt, formValues.roles]);

  const submit = (formData: any): void => {
    // @NOTE-EV: we can't use ReduxForm onSubmitFail prop since combining it with persistentSubmitErrors breaks its behavior
    if (status !== 'draft' && invalid) {
      scrollToError(errors);
      // @NOTE-EV: needs to be thrown manually because FieldArray relies on `submitFail` bool to display error message when the user hasn't interacted with the section.
      throw new SubmissionError({});
    } else {
      const data = formData.toJS();
      dispatch(Actions.saveEventAction({ ...data, manual: past, deedType, timeZone }));
    }
  };

  const handleFormSubmit = (formData: any): void => {
    if ((eventDuration ?? 0) >= 12 && !warningModalOpen) {
      setPendingSubmission(formData);
      setWarningModalOpen(true);
      return;
    }

    submit(formData);
  };

  const handleContinueSubmit = () => {
    setWarningModalOpen(false);
    if (pendingSubmission) {
      submit(pendingSubmission);
      setPendingSubmission(null);
    }
  };

  const handleCancelSubmit = () => {
    setWarningModalOpen(false);
    setPendingSubmission(null);
    // Scroll to time settings section
    const timeSettingsElement = document.querySelector(
      '[data-step="Event Date and Time Information *"]'
    );

    if (timeSettingsElement) {
      const headerHeight = 80;
      const padding = 100;

      timeSettingsElement.scrollIntoView({ behavior: 'smooth' });

      // Then after a small delay, adjust for header height
      setTimeout(() => {
        const elementPosition = timeSettingsElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition - headerHeight - padding;

        window.scrollBy({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  const initialSharedWithCompanies = List(
    user?.organization?.siblingSubsidiaries?.map((company) => company.id).filter(Boolean) || []
  );

  const isAdmin = user?.type === 'Admin';
  const partnerPillars = pillars.filter(
    (pillar) =>
      !!pillar &&
      (isCompany ||
        pillar.organization === formValues.partner ||
        (isAdmin && pillar.individualsOnly))
  );
  const partnerERGs = ERGs.filter(
    (ERG) =>
      !!ERG &&
      !isCommunityFeatureEnabled &&
      (isCompany || ERG.organization === formValues.partner || (isAdmin && ERG.individualsOnly))
  );
  const showCampaignsAndPillarsAndERGs =
    (isAdmin || isCompany || formValues.partner) &&
    (campaigns.size || partnerPillars.size || partnerERGs.size || communities.size);

  if (!past) {
    const publicToolTip =
      "Selecting 'Public' will enable any individual to view this event, users will still be required to login to register. Additionally, this event will be publicly available to all Deed users.";
    const companyToolTip = isCompany
      ? "Selecting 'Company Only' will require users to login to verify they are company employees before they can view or register for this event."
      : "Selecting 'Company Only' will require users to login to verify they are company employees of the Partner Company selected above before they can view or register for this event.";

    const getStatusText = (rawStatus: string): string => {
      switch (rawStatus) {
        case 'draft':
          return 'Save draft';

        case 'pending':
          return 'Submit for review';

        default:
          return 'Submit';
      }
    };

    const steps: AutoStep[] = [
      {
        labelText: 'Event Location *',
        id: 'step-event-location',
        children: (
          <>
            <Field
              name="virtual"
              component={FormCheckbox}
              label="This event will be hosted virtually"
              disabled={isExternal}
            />
            {formValues.virtual && (
              <Field
                disabled={isExternal}
                name="externalLink"
                component={FormTextField}
                label="Enter event meeting link (e.g. Zoom, Google Meet, Skype etc.) *"
                fullWidth
                validate={[required, isURL]}
              />
            )}
            {!formValues.virtual && (
              <Field
                name="location"
                autoFocus
                component={FormLocationField}
                fullWidth
                label="Where is your Event located? *"
                helperText={!isExternal && 'Please enter a full address'}
                autoComplete="off"
                disabled={isExternal}
                validate={[required, isAddress]}
                afterSelect={handleLocationSelect}
                locations={user?.locations}
                sx={{ textTransform: 'uppercase' }}
              />
            )}
            <FormControl style={{ width: '100%', height: 20, marginTop: 30 }}>
              <InputLabel shrink>
                If this event should be available only to users from a specific location, select the
                location here:
              </InputLabel>
            </FormControl>
            <Field
              name="allowedLocations"
              component={FormLocationSelector}
              disabled={isExternal}
              props={{ locations, expanded: false }}
              style={{ marginRight: 10, marginBottom: 10 }}
              fullWidth
            />
          </>
        ),
      },
      {
        labelText: 'Event Date and Time Information *',
        id: 'step-event-date-time',
        children: (
          <div data-step="Event Date and Time Information *">
            {(errors.startingAt || errors.endingAt) && (
              <input
                disabled
                name={errors.startingAt ? 'startingAt' : 'endingAt'}
                style={{ height: 0, width: 0, display: 'block' }}
              />
            )}
            <div>
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>Event Date, Start and End Time *</InputLabel>
              </FormControl>
              <Fields
                disabled={isExternal}
                names={['startingAt', 'endingAt']}
                isPreviousDateDisabled
                component={FormDateWithStartAndEndTime}
                validate={{
                  startingAt: required,
                  endingAt: required,
                }}
                timeZone={timeZone}
                setTimeZone={setTimeZone}
                timeZoneDisabled={!formValues.virtual || isExternal}
                allowClear={formValues.roles?.size < 1}
              />
              {timeZone &&
                zonedTimeToUtc(formValues.endingAt?.toDate?.() ?? formValues.endingAt, timeZone) <
                  new Date() && (
                  <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
                    Invalid time: Date/time in past
                  </FormLabel>
                )}
              <EventDurationText eventDuration={eventDuration} />
            </div>

            <RegistrationDeadline
              isNew={formValues.id === 'new'}
              timeZone={timeZone}
              endingAt={formValues.endingAt}
            />

            <div>
              <h3>Shifts</h3>
              <FieldArray
                name="roles"
                component={FormRoles}
                disabled={isExternal || !formValues.endingAt || !formValues.startingAt}
                formValues={formValues}
                deed={deed}
                selectedDeedType={deedType}
                timeZone={timeZone}
                labelText="Which shifts are needed for your Event?"
              />
            </div>
          </div>
        ),
      },
      {
        labelText: 'Available spots *',
        id: 'step-event-available-spots',
        children: (
          <>
            <Field
              name="attendeeLimit"
              component={FormTextField}
              type="number"
              disabled={
                Boolean(isExternal && formValues.attendeeLimit) ||
                Boolean(formValues.roles?.size > 0)
              }
              inputProps={{ min: '0', step: 1 }}
              fullWidth
              label="How many people will be needed? *"
              helperText={
                !isExternal && "Overestimate! It's good to enter about 20% more than what you need."
              }
              autoComplete="off"
              validate={[required, isNumber]}
            />
            <Field
              name="waitlistLimit"
              component={DisableWaitlistCheckbox}
              label="Disable waitlist"
            />
          </>
        ),
      },
      {
        labelText: 'Event Name *',
        id: 'step-event-name',
        children: (
          <Field
            name="name"
            disabled={isExternal}
            component={FormTextField}
            fullWidth
            inputProps={{ maxLength: 100, autoComplete: 'off' }}
            label="What would you like to name your event? *"
            helperText={
              !isExternal && 'Choose an exciting name for your event! (40 characters max)'
            }
            validate={required}
            autoComplete="off"
          />
        ),
      },
      {
        labelText: 'Event Description',
        id: 'step-event-description',
        children: isExternal ? (
          <>
            <p
              style={{
                color: 'black',
                opacity: 0.38,
                textTransform: 'uppercase',
                fontSize: 12,
              }}
            >
              How would you describe your event?
            </p>
            <RenderHtml
              source={{ html: formValues.description || '' }}
              customStyles={{ color: 'black', opacity: 0.38 }}
            />
          </>
        ) : (
          <Field
            name="description"
            component={FormDescriptionRteField}
            fullWidth
            multiline
            inputProps={{ maxLength: 5000, autoComplete: 'off' }}
            label="How would you describe your event?"
            helperText="Give a description for your event! (5000 characters max)"
            autoComplete="off"
            validate={maxLength5000}
          />
        ),
      },
      {
        labelText: 'Event Point of Contact *',
        id: 'step-event-point-of-contact',
        children: (
          <>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>
                Please provide a point of contact for this Deed who will serve as a liaison for
                registered volunteers
              </InputLabel>
            </FormControl>
            <Grid container spacing={3}>
              <Grid item md>
                <Field
                  id="organizerName"
                  disabled={isExternal}
                  name="organizerName"
                  component={FormTextField}
                  fullWidth
                  label="Name *"
                  validate={required}
                  autoComplete="off"
                />
              </Grid>
              <Grid item md>
                <Field
                  id="organizerEmail"
                  name="organizerEmail"
                  type="email"
                  component={FormTextField}
                  fullWidth
                  label={`Email${!isExternal ? ' *' : ''}`}
                  validate={!isExternal && [required, isEmail]}
                  disabled={isExternal}
                  autoComplete="off"
                />
              </Grid>
              <Grid item md>
                <Field
                  id="organizerPhone"
                  name="organizerPhone"
                  type="tel"
                  component={PhoneNumber}
                  fullWidth
                  label="Phone Number"
                  validate={!isExternal && isPhone}
                  disabled={isExternal}
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          </>
        ),
      },
      !!showCampaignsAndPillarsAndERGs && {
        labelText: `${!isCompany ? 'Partner' : 'Campaigns,'} Pillars & ${
          isCommunityFeatureEnabled ? 'Communities' : 'ERGs'
        }`,
        id: 'step-campaigns-pillars-ergs',
        children: (
          <>
            <SelectOrganizingCommunity communities={communities} />
            {campaigns.size > 0 && (
              <>
                <FieldArray
                  name="campaigns"
                  component={FormValueSelector}
                  selectMultiple
                  options={campaigns}
                  labelText="Include in campaign"
                />
                <br />
                <br />
              </>
            )}
            {partnerPillars.size > 0 && (
              <FieldArray
                name="pillars"
                component={FormValueSelector}
                options={partnerPillars}
                labelText="Which cause area best describes your deed? (Select a maximum of five.)"
                selectMultiple
                validate={maxLength5}
                maxAmountAllowed={5}
              />
            )}
            {partnerERGs.size > 0 && (
              <>
                <br />
                <br />
                <FieldArray
                  name="ERGs"
                  component={FormValueSelector}
                  selectMultiple
                  options={partnerERGs}
                  labelText="Which ERGs match your Deed? (you may choose more than one)"
                />
              </>
            )}
            <SelectCommunities communities={communities} />
          </>
        ),
      },
      {
        labelText: `Event Categories${SDGs.size > 0 ? ' & SDGs' : ''} *`,
        id: 'step-event-categories-sdgs',
        children: (
          <>
            {errors.categories && (
              <input disabled name="categories" style={{ height: 0, width: 0, display: 'block' }} />
            )}
            <FieldArray
              name="categories"
              component={FormValueSelector}
              selectMultiple
              options={categories}
              labelText="Which categories best describe your deed? (Select a maximum of five.) *"
              validate={categories.size > 0 ? [notEmpty, maxLength5] : undefined}
            />
            {SDGs.size > 0 && (
              <>
                <br />
                <br />
                {errors.SDGs && (
                  <input disabled name="SDGs" style={{ height: 0, width: 0, display: 'block' }} />
                )}
                <FieldArray
                  name="SDGs"
                  component={FormValueSelector}
                  selectMultiple
                  options={SDGs}
                  labelText="Which UN SDGs best fit your deed? (Select a maximum of five.) *"
                  validate={isExternal ? maxLength5 : [notEmpty, maxLength5]}
                />
              </>
            )}
          </>
        ),
      },
      {
        labelText: 'Event Picture *',
        id: 'step-event-picture',
        children: (
          <>
            {isExternal && !isGoodera && formValues.mainPicture !== '' ? (
              <img
                style={{ maxWidth: '500px' }}
                src={formValues.mainPicture}
                alt={formValues.name || ''}
              />
            ) : (
              <FieldArray
                name="pictures"
                component={FormPictureSelector}
                options={pictures}
                pictures={formValues.pictures}
                fileUploadedAction={(file: any) => dispatch(Actions.fileUploadedAction(file))}
                uploadedFile={uploadedFile}
                selectedCategories={formValues.categories}
                labelText="Choose a photo for your Deed (you may choose one of the preselected ones below or upload your own) *"
                validate={notEmpty}
              />
            )}
          </>
        ),
      },
      {
        labelText: isCompany ? 'Nonprofit information' : 'Partner company information',
        id: 'step-nonprofit-partner-info',
        children: (
          <>
            <ProvisionalNonprofitDataBanner
              organizationId={formValues.organization as string}
              provisionalNonprofitData={deed?.provisionalNonprofitData}
            />
            <Fields
              names={[
                'organization',
                'partner',
                'public',
                'partnerName',
                'partnerEmail',
                'partnerLocation',
                'partnerEin',
                'partnerPhone',
              ]}
              required={false}
              component={FormPartnerSelector}
              fetchPartnersAction={(
                search: unknown,
                countryCode: unknown,
                acceptsDonations: boolean
              ) => dispatch(Actions.fetchPartnersAction(search, countryCode, acceptsDonations))}
              partnersLoading={partnersLoading}
              partners={partners}
              isCompany={isCompany}
              featured={formValues.featured}
              selectedOrganization={deed?.organization}
              user={user}
              initialSelectedSearchTerm={deed?.provisionalNonprofitData?.name || ''}
              sx={{ textTransform: 'uppercase' }}
            />
          </>
        ),
      },
      {
        labelText: 'Volunteer Information *',
        id: 'step-volunteer-information',
        children: (
          <>
            {!isExternal && (
              <>
                <Field
                  name="duties"
                  component={FormTextField}
                  inputProps={{ maxLength: 1000 }}
                  fullWidth
                  multiline
                  label="What are the volunteer duties? *"
                  helperText="Please give a brief description of what volunteers will be helping with (1-2 sentences max)"
                  validate={required}
                  autoComplete="off"
                />
                <br />
                <br />
              </>
            )}
            <Field
              name="mustKnows"
              disabled={isExternal}
              component={FormTextField}
              inputProps={{ maxLength: 500 }}
              fullWidth
              multiline
              label="What are the must knows?"
              helperText={
                !isExternal &&
                'Please provide any essential information that volunteers should be aware of'
              }
              autoComplete="off"
            />
          </>
        ),
      },
      !isExternal && {
        labelText: 'Payment Information',
        id: 'step-payment-information',
        children: (
          <Fields
            names={['donationText', 'donationLink']}
            component={FormDoubleTextFieldWithCheckbox}
            firstFieldDefault={formValues.donationText}
            secondFieldDefault={formValues.donationLink}
            checkboxLabel="Is a volunteer donation required?"
            firstFieldProps={{
              name: 'donationText',
              label: 'Donation description',
              helperText: 'How much? How to?',
              defaultValue: formValues.donationText,
            }}
            secondFieldProps={{
              name: 'donationLink',
              label: 'Online payment link',
              checkboxLabel: 'Can volunteers pay online?',
              defaultValue: formValues.donationLink,
              validate: isURL,
            }}
          />
        ),
      },
      {
        labelText: 'Additional Event Settings',
        id: 'step-additional-settings',
        children: (
          <>
            {isCompany && (
              <>
                <Field name="featured" component={FormCheckbox} label="Featured Deed?" />
                <br />
              </>
            )}
            <Field
              name="linkOnly"
              component={FormCheckbox}
              label="Link only: only users with access to the link can view"
            />
            {!isExternal && (
              <Field
                name="backgroundCheckLink"
                component={FormTextFieldWithCheckbox}
                defaultValue={formValues.backgroundCheckLink}
                label="Background check link"
                checkboxLabel="Is a volunteer background check required?"
                validate={isURL}
              />
            )}
          </>
        ),
      },
      !isExternal && {
        labelText: 'Volunteer Forms',
        id: 'step-volunteer-forms',
        children: (
          <>
            <FieldArray
              name="forms"
              component={FormFormsSelector}
              options={forms}
              labelText="Choose the forms which would be required for a volunteer to fill in. Go to the Forms tab to create new forms."
            />
            {organization?.parent && (
              <Field
                name="shareFormResponses"
                component={FormCheckbox}
                label="Allow form responses to be visible to other organizations"
              />
            )}
            {nonprofitForms && nonprofitForms.size > 0 && (
              <Alert severity="info" style={{ marginTop: 20 }}>
                {`This deed is associated with the following form(s) created by the nonprofit ${
                  deed?.organization.name
                }:
                ${nonprofitForms?.map((form) => form.name).join(', ')}`}
              </Alert>
            )}
          </>
        ),
      },
      !isExternal && {
        labelText: 'Additional Volunteer Questions',
        id: 'step-additional-questions',
        children: <FieldArray name="formQuestions" component={FormEditor} change={change} />,
      },
      (!ensureCompanyOnlyDeeds || Boolean(user?.organization?.parent)) && {
        labelText: 'Deed Visibility *',
        id: 'step-deed-visibility',
        children: (
          <>
            <Field
              name="companyOrPublic"
              component={FormRadioButton}
              validate={requiredRadioButton}
            >
              <RadioGroup row>
                <Tooltip title={companyToolTip}>
                  <FormControlLabel
                    control={<Radio />}
                    value="company"
                    label="Company Only"
                    checked={formValues.companyOrPublic === 'company'}
                    disabled={!isCompany && !formValues.partner}
                  />
                </Tooltip>
                <Tooltip title={publicToolTip}>
                  <FormControlLabel
                    control={<Radio />}
                    value="public"
                    label="Public"
                    checked={formValues.companyOrPublic === 'public'}
                    disabled={ensureCompanyOnlyDeeds}
                  />
                </Tooltip>
                {user?.organization?.parent && (
                  <Tooltip title={shareTooltip}>
                    <FormControlLabel
                      control={<Radio />}
                      value="shared"
                      label="Share with other Organizations"
                      checked={formValues.companyOrPublic === 'shared'}
                      disabled={!isCompany}
                    />
                  </Tooltip>
                )}
              </RadioGroup>
            </Field>
            {isCompany && user?.organization?.parent && formValues.companyOrPublic === 'shared' && (
              <Field
                name="sharedWithCompanies"
                component={FormSelectedAndOptedInCompanies}
                optedInCompanies={duplicate ? List() : deed?.optedInCompanies}
                sharedWithCompanies={formValues.sharedWithCompanies || initialSharedWithCompanies}
                validate={notEmpty}
              />
            )}
          </>
        ),
      },
      {
        labelText: 'Review',
        id: 'step-review',
        children: (
          <div>
            <p>
              {enableValidation && invalid ? (
                <span style={{ color: red }}>
                  Uh oh - there is missing information that is important for volunteers to know!
                  Before you are able to publish this deed, you&apos; need to finish completing the
                  form. For now, your deed can be saved as a draft.
                </span>
              ) : (
                <>
                  Take time to review the information you entered. The deed can be set to pending or
                  published only when all mandatory sections have been filled correctly.
                </>
              )}
            </p>
            <br />
            <br />
            <Field
              name="status"
              component={FormPublishingStatus}
              label="Publishing status:"
              deedApprovalRequired={deedApprovalRequired}
            />
            <br />
            <br />
            <Button type="submit" color="primary" variant="contained">
              {getStatusText(status)}
            </Button>
          </div>
        ),
      },
    ];

    return organization ? (
      <>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <AutoStepper steps={steps} />
        </form>
        <LongEventWarningModal
          open={warningModalOpen}
          onEdit={handleCancelSubmit}
          onContinue={handleContinueSubmit}
        />
      </>
    ) : null;
  }

  const steps: AutoStep[] = [
    {
      labelText: 'Event Location *',
      id: 'step-event-location',
      children: (
        <>
          <Field
            name="location"
            component={FormLocationField}
            fullWidth
            autoFocus
            label="Where was your Event located?"
            helperText="Please enter a full address"
            autoComplete="off"
            validate={[required, isAddress]}
            locations={user?.locations}
            sx={{ textTransform: 'uppercase' }}
          />
        </>
      ),
    },
    {
      labelText: 'Event Name *',
      id: 'step-event-name',
      children: (
        <>
          <Field
            name="name"
            component={FormTextField}
            fullWidth
            inputProps={{ maxLength: 100 }}
            label="What would you like to name your Deed? *"
            helperText="Choose an exciting name for your Deed! (40 characters max)"
            validate={required}
            autoComplete="off"
          />
        </>
      ),
    },
    {
      labelText: 'Event Description',
      id: 'step-event-description',
      children: (
        <>
          <Field
            name="description"
            component={FormDescriptionRteField}
            fullWidth
            multiline
            inputProps={{ maxLength: 5000, autoComplete: 'off' }}
            label="How would you describe your event?"
            helperText="Give a description for your event! (5000 characters max)"
            validate={maxLength5000}
            autoComplete="off"
          />
        </>
      ),
    },
    !!showCampaignsAndPillarsAndERGs && {
      labelText: `${!isCompany ? 'Partner' : 'Campaigns,'} Pillars & ${
        isCommunityFeatureEnabled ? 'Communities' : 'ERGs'
      }`,
      id: 'step-campaigns-pillars-ergs',
      children: (
        <>
          <SelectOrganizingCommunity communities={communities} />
          {campaigns.size > 0 && (
            <>
              <FieldArray
                name="campaigns"
                component={FormValueSelector}
                selectMultiple
                options={campaigns}
                labelText="Include in campaign"
              />
              <br />
              <br />
            </>
          )}
          {partnerPillars.size > 0 && (
            <FieldArray
              name="pillars"
              component={FormValueSelector}
              options={partnerPillars}
              labelText="Which cause area best describes your deed? (Select a maximum of five.)"
              selectMultiple
              validate={maxLength5}
              maxAmountAllowed={5}
            />
          )}
          {partnerERGs.size > 0 && (
            <>
              <br />
              <br />
              <FieldArray
                name="ERGs"
                component={FormValueSelector}
                selectMultiple
                options={partnerERGs}
                labelText="Which ERGs match your Deed? (you may choose more than one)"
              />
            </>
          )}
          <SelectCommunities communities={communities} />
        </>
      ),
    },
    {
      labelText: 'Event Categories & SDGs *',
      id: 'step-event-categories-sdgs',
      children: (
        <>
          <FieldArray
            name="categories"
            component={FormValueSelector}
            selectMultiple
            options={categories}
            labelText="Which categories best describe your Deed? (you may choose more than one, but no more than three)"
            validate={categories.size > 0 ? [notEmpty, maxLength5] : undefined}
          />
          {SDGs.size > 0 && (
            <>
              <br />
              <br />
              <FieldArray
                name="SDGs"
                component={FormValueSelector}
                selectMultiple
                options={SDGs}
                labelText="Which UN SDGs best fit your Deed? (you may choose more than one, but no more than three)"
                validate={[notEmpty, maxLength5]}
              />
            </>
          )}
        </>
      ),
    },
    {
      labelText: 'Event Picture *',
      id: 'step-event-picture',
      children: (
        <>
          <FieldArray
            name="pictures"
            component={FormPictureSelector}
            options={pictures}
            pictures={formValues.pictures}
            fileUploadedAction={(file: any) => dispatch(Actions.fileUploadedAction(file))}
            uploadedFile={uploadedFile}
            selectedCategories={formValues.categories}
            labelText="Choose a photo for your Deed (you may choose one of the preselected ones below or upload your own)"
            validate={notEmpty}
          />
          {errors.pictures && +(formValues.startingAt || formValues.endingAt) > 4 && (
            <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
              Required
            </FormLabel>
          )}
        </>
      ),
    },
    {
      labelText: 'Date, Start and End Time *',
      id: 'step-event-date-time',
      children: (
        <>
          {(errors.startingAt || errors.endingAt) && (
            <input
              disabled
              name={errors.startingAt ? 'startingAt' : 'endingAt'}
              style={{ height: 0, width: 0, display: 'block' }}
            />
          )}
          <Fields
            isExternal={isExternal}
            names={['startingAt', 'endingAt']}
            isPreviousDateDisabled={false}
            isNextDateDisabled
            component={FormDateWithStartAndEndTime}
            setTimeZone={setTimeZone}
            timeZone={timeZone}
            timeZoneDisabled={!formValues.virtual || isExternal}
            validate={{
              startingAt: required,
              endingAt: required,
            }}
          />
        </>
      ),
    },
    !isExternal && {
      labelText: 'Volunteer Information *',
      id: 'step-volunteer-information',
      children: (
        <>
          <Field
            name="duties"
            component={FormTextField}
            inputProps={{ maxLength: 1000 }}
            fullWidth
            multiline
            label="What were the volunteer duties? *"
            helperText="Please give a brief description of what volunteers helped with (1-2 sentences max)"
            validate={required}
            autoComplete="off"
          />
        </>
      ),
    },
    {
      labelText: isCompany ? 'Nonprofit information' : 'Partner company information',
      id: 'step-nonprofit-partner-info',
      children: (
        <>
          <Fields
            names={[
              'organization',
              'partner',
              'public',
              'partnerName',
              'partnerEmail',
              'partnerLocation',
              'partnerEin',
              'partnerPhone',
            ]}
            required={false}
            component={FormPartnerSelector}
            fetchPartnersAction={(
              search: unknown,
              countryCode: unknown,
              acceptsDonations: boolean
            ) => dispatch(Actions.fetchPartnersAction(search, countryCode, acceptsDonations))}
            partnersLoading={partnersLoading}
            partners={partners}
            isCompany={isCompany}
            past
            featured={formValues.featured}
            selectedOrganization={deed?.organization}
            user={user}
          />
        </>
      ),
    },
    {
      labelText: 'Review',
      id: 'step-review',
      children: (
        <>
          <div>
            <p>
              {(dirty || deedId) && invalid ? (
                <span style={{ color: red }}>
                  Uh oh – there is missing information that is important for your statistics
                  tracking!
                </span>
              ) : (
                <>Take time to review the information you entered.</>
              )}
              <br />
              <br />
              <Field
                name="status"
                component={FormPublishingStatus}
                label="Publishing status:"
                deedApprovalRequired={deedApprovalRequired}
              />
              <br />
              <br />
              <Button type="submit" color="primary" variant="contained">
                Submit
              </Button>
            </p>
          </div>
        </>
      ),
    },
  ];

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <AutoStepper steps={steps} />
    </form>
  );
};

export default reduxForm<FormData>({
  form: FORM_IDENTIFIER,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  persistentSubmitErrors: true,
})(PureRender(DeedVolunteerForm));
